import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SeatShape } from "../../components/SeatMap/seatPositions";
import { apiDelete, apiGet, apiPatch, apiPost } from "../../lib/api-client";

export interface AdminSeat {
  seatId: number;
  label: string;
  isActive: boolean;
  xPercent?: number;
  yPercent?: number;
  widthPercent?: number;
  heightPercent?: number;
  shape?: SeatShape;
  area?: string;
  createdAt: string;
  updatedAt: string;
}

interface AdminSeatsResponse {
  seats: AdminSeat[];
}

export interface UpdateSeatPositionInput {
  seatId: number;
  xPercent?: number;
  yPercent?: number;
  widthPercent?: number;
  heightPercent?: number;
  shape?: SeatShape;
  area?: string;
  label?: string;
}

export function useAdminSeats() {
  return useQuery({
    queryKey: ["admin", "seats"],
    queryFn: async () => {
      const response = await apiGet<AdminSeatsResponse>("/admin/seats");
      return response.seats;
    },
  });
}

export function useUpdateSeatPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateSeatPositionInput) => {
      const { seatId, ...data } = input;
      const response = await apiPatch<{ seat: AdminSeat }>(
        `/admin/seats/${seatId}`,
        data
      );
      return response.seat;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "seats"] });
      queryClient.invalidateQueries({ queryKey: ["seats"] });
    },
  });
}

export interface CreateSeatInput {
  label: string;
  xPercent: number;
  yPercent: number;
  widthPercent?: number;
  heightPercent?: number;
  shape?: SeatShape;
  area?: string;
}

export function useCreateSeat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSeatInput) => {
      const response = await apiPost<{ seat: AdminSeat }>(
        "/admin/seats",
        input
      );
      return response.seat;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "seats"] });
      queryClient.invalidateQueries({ queryKey: ["seats"] });
    },
  });
}

interface DeleteSeatResponse {
  success: boolean;
  deletedReservations: number;
}

export function useDeleteSeat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (seatId: number) => {
      const response = await apiDelete<DeleteSeatResponse>(
        `/admin/seats/${seatId}`
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "seats"] });
      queryClient.invalidateQueries({ queryKey: ["seats"] });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
    },
  });
}
